/**
 * The contract-template editor: one template, two languages.
 *
 * Lives inside the Templates tab rather than on a route of its own, so closing
 * it returns to the list without touching history. The language switch is the
 * shell's `Seg` - the same sliding thumb the account menu uses for theme and
 * locale - instead of the hand-rolled pill pair this screen used to carry.
 */
import { useState } from "react";
import { ChevronLeft, Loader2, Lock, Save } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  createContractTemplate, updateContractTemplate,
  type ContractTemplateFull, type ContractTemplateInput,
} from "../../../lib/projects";
import { RichTextEditor } from "../../../components/RichTextEditor";
import { Btn, Card, CardHead, StatusPill } from "../../ui";
import { Seg } from "../../shell/Seg";

type Loc = Record<string, string>;

/** The two languages a template body is written in. Estonian is the one the API
 *  requires (see `localized` in contracts.routes.ts); English is optional. */
const LANGS = ["et", "en"] as const;
type Lang = (typeof LANGS)[number];

/** What a template body may interpolate, shown in the editor as a reference.
 *  Filled in when a contract is created from the template, not here. */
const PLACEHOLDERS = [
  "client_name", "client_reg", "client_vat", "client_address",
  "company_name", "company_reg", "company_vat", "company_address",
  "company_email", "company_phone", "company_website", "value", "date", "year",
];

const BLANK: Loc = { et: "", en: "" };

export function TemplateEditor({
  initial,
  onClose,
  onSaved,
}: {
  /** null = a new template, a record = editing that one. */
  initial: ContractTemplateFull | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const [lang, setLang] = useState<Lang>("et");
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState<Loc>(initial?.name ?? BLANK);
  const [description, setDescription] = useState<Loc>(initial?.description ?? BLANK);
  const [type, setType] = useState<Loc>(initial?.type ?? BLANK);
  const [body, setBody] = useState<Loc>(initial?.body ?? BLANK);
  const [value, setValue] = useState(
    initial?.defaultValueCents != null ? String(initial.defaultValueCents / 100) : "",
  );
  const [duration, setDuration] = useState(initial?.durationMonths != null ? String(initial.durationMonths) : "");

  const setLoc = (setter: (v: Loc) => void, cur: Loc, v: string) => setter({ ...cur, [lang]: v });
  const nameEt = (name.et ?? "").trim();

  async function save() {
    // The API rejects a template with no Estonian name, so the switch flips to
    // the tab that is actually missing something rather than failing silently
    // on a language the operator cannot see.
    if (!nameEt) {
      setLang("et");
      return;
    }
    setSaving(true);
    try {
      const payload: ContractTemplateInput = {
        name,
        description,
        type,
        body,
        defaultValueCents: value ? Math.round(parseFloat(value) * 100) : null,
        durationMonths: duration ? parseInt(duration, 10) : null,
      };
      if (initial) await updateContractTemplate(initial.key, payload);
      else await createContractTemplate(payload);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={onClose}
        className="crm-mut mb-3 inline-flex items-center gap-1 text-[12.5px] hover:text-foreground"
      >
        <ChevronLeft className="size-3.5" />
        {t("templates.backToList")}
      </button>

      <Card>
        <CardHead
          title={
            <span className="flex flex-wrap items-center gap-2.5">
              {initial ? t("templates.editTitle") : t("templates.newTitle")}
              {initial?.builtin ? (
                <StatusPill tone="mut">
                  <Lock className="size-3" />
                  {t("templates.builtin")}
                </StatusPill>
              ) : null}
            </span>
          }
          actions={
            <Seg
              value={lang}
              onChange={setLang}
              label={t("templates.language")}
              options={LANGS.map((l) => ({ value: l, label: l.toUpperCase(), title: l.toUpperCase() }))}
            />
          }
        />

        {/* `crm-card` is a surface and nothing else - no padding of its own - so
            a body that is not a `crm-rows` has to say where its edges are. */}
        <div className="px-6 pb-5 pt-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <span className="crm-lab">
              {t("templates.name")} {lang === "et" ? "*" : ""}
            </span>
            <label className="crm-inp">
              <input
                value={name[lang] ?? ""}
                onChange={(e) => setLoc(setName, name, e.target.value)}
                placeholder={t("templates.namePlaceholder")}
                autoComplete="off"
              />
            </label>
          </div>

          <div className="sm:col-span-2">
            <span className="crm-lab">{t("templates.description")}</span>
            <label className="crm-inp">
              <input
                value={description[lang] ?? ""}
                onChange={(e) => setLoc(setDescription, description, e.target.value)}
                placeholder={t("templates.descriptionPlaceholder")}
                autoComplete="off"
              />
            </label>
          </div>

          <div>
            <span className="crm-lab">{t("templates.type")}</span>
            <label className="crm-inp">
              <input
                value={type[lang] ?? ""}
                onChange={(e) => setLoc(setType, type, e.target.value)}
                placeholder={t("templates.typePlaceholder")}
                autoComplete="off"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="crm-lab">{t("templates.defaultValue")} (EUR)</span>
              <label className="crm-inp">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t("templates.byAgreement")}
                />
              </label>
            </div>
            <div>
              <span className="crm-lab">{t("templates.durationMonths")}</span>
              <label className="crm-inp">
                <input
                  type="number"
                  min="0"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="-"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="mt-3.5">
          <span className="crm-lab">
            {t("templates.body")} ({lang.toUpperCase()})
          </span>
          {/* Keyed by language so switching swaps the document rather than
              feeding new text into an editor that is holding its own state. */}
          <RichTextEditor
            key={lang}
            value={body[lang] ?? ""}
            onChange={(v) => setLoc(setBody, body, v)}
            placeholder={t("templates.bodyPlaceholder")}
          />

          <details className="crm-fnote mt-2">
            <summary className="cursor-pointer select-none">{t("templates.placeholdersHint")}</summary>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {PLACEHOLDERS.map((p) => (
                <code key={p} className="crm-chip crm-tnum">{`{{${p}}}`}</code>
              ))}
            </div>
          </details>
        </div>

        <div className="mt-3.5 flex justify-end gap-2.5">
          <Btn type="button" onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Btn>
          <Btn tone="dark" onClick={() => void save()} disabled={saving || !nameEt}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            {t("common.save")}
          </Btn>
        </div>
        </div>
      </Card>
    </>
  );
}
